import { Z_INDEX } from "./zIndex"

// Central registry of open popups (menus, dialogs, dropdown lists).
//
// Each popup is linked to the popup it was opened from through provide/inject,
// i.e. by the Vue component tree rather than the DOM tree. Popup content is
// rendered detached in #popup-layer, so DOM containment alone can't tell that a
// click inside a child popup belongs to its parent.
//
// - Outside click: one capturing mousedown listener closes every open popup
//   that is neither the clicked popup nor one of its ancestors.
// - Escape: closes only the topmost popup.
// - z-index: every registered popup gets a value above the previous topmost one.
// - Scroll lock: body scrolling is disabled while any popup with lockScroll is open.

const stack = []
let listening = false
let pointerDown = { top: null, target: null }
let savedBodyOverflow = null

// Overlay layers inside #modal-root (public/index.html): popup-layer,
// notification-layer, tooltip-layer
export function getLayer (id) {
	return document.getElementById(id) || document.body
}

export function getPopupContainer () {
	return getLayer("popup-layer")
}

function isSelfOrAncestor (candidate, popup) {
	for (var p = popup; p; p = p.parent) {
		if (p === candidate) return true
	}
	return false
}

function containsTarget (popup, target) {
	return popup.elements().some((el) => el && el.contains(target))
}

function findPopupAt (target) {
	for (var i = stack.length - 1; i >= 0; i--) {
		if (containsTarget(stack[i], target)) return stack[i]
	}
	return null
}

function onMouseDown (e) {
	pointerDown = { top: stack[stack.length - 1] || null, target: e.target }
	var hit = findPopupAt(e.target)
	stack.slice().reverse().forEach((popup) => {
		// Closing a popup may already have removed others (e.g. destroyed children)
		if (stack.indexOf(popup) === -1) return
		if (hit && isSelfOrAncestor(popup, hit)) return
		popup.onOutsideClick(e)
	})
}

function onKeydown (e) {
	if (e.key !== "Escape" || e.defaultPrevented) return
	var top = stack[stack.length - 1]
	if (top) top.onEscape(e)
}

function ensureListeners () {
	if (listening) return
	listening = true
	document.addEventListener("mousedown", onMouseDown, true)
	// Bubble phase so popup content can handle Escape itself with .stop / .prevent
	document.addEventListener("keydown", onKeydown)
}

function updateScrollLock () {
	var locked = stack.some((p) => p.lockScroll)
	if (locked && savedBodyOverflow === null) {
		savedBodyOverflow = document.body.style.overflow
		document.body.style.overflow = "hidden"
	} else if (!locked && savedBodyOverflow !== null) {
		document.body.style.overflow = savedBodyOverflow
		savedBodyOverflow = null
	}
}

export function register (popup) {
	if (stack.indexOf(popup) !== -1) return popup.zIndex
	var top = stack[stack.length - 1]
	popup.zIndex = (top ? top.zIndex : Z_INDEX.popupBase) + Z_INDEX.popupStep
	stack.push(popup)
	ensureListeners()
	updateScrollLock()
	return popup.zIndex
}

export function unregister (popup) {
	var index = stack.indexOf(popup)
	if (index === -1) return
	stack.splice(index, 1)
	if (pointerDown.top === popup) pointerDown.top = null
	updateScrollLock()
}

export function isRegistered (popup) {
	return stack.indexOf(popup) !== -1
}

// True when the last mousedown happened on `el` while `popup` was the topmost
// popup. Used for backdrop clicks: the press must start on the backdrop (not a
// drag that ends there) and must not be the click that dismissed a child popup.
export function wasPressedOn (popup, el) {
	return pointerDown.top === popup && pointerDown.target === el
}

// Shared by popup components. A component using it implements:
//   popupElements ()        -> elements that count as "inside" (activator, content...)
//   onPopupOutsideClick (e) -> optional
//   onPopupEscape (e)       -> optional
// and calls registerPopup() when it opens and unregisterPopup() when it closes.
export const popupMixin = {
	inject: {
		ksParentPopup: { from: "ksPopup", default: null }
	},
	provide () {
		var vm = this
		// Plain (non-reactive) object on purpose; it is shared with descendants.
		this.popupHandle = {
			parent: this.ksParentPopup,
			zIndex: 0,
			lockScroll: false,
			elements: () => (vm.popupElements ? vm.popupElements() : []),
			onOutsideClick: (e) => { if (vm.onPopupOutsideClick) vm.onPopupOutsideClick(e) },
			onEscape: (e) => { if (vm.onPopupEscape) vm.onPopupEscape(e) }
		}
		return { ksPopup: this.popupHandle }
	},
	methods: {
		registerPopup (options) {
			if (options) Object.assign(this.popupHandle, options)
			return register(this.popupHandle)
		},
		unregisterPopup () {
			unregister(this.popupHandle)
		}
	},
	beforeDestroy () {
		unregister(this.popupHandle)
	}
}
